/**
 * PublicLegalGraph — /legal-graph
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';
import { useStats, type DomaineDetail } from '@/hooks/useStats';
import { Loader } from '@/components/Loader';

const SITE_URL = 'https://smartlawyer.ai';

const DOMAINE_ICONS: Record<string, string> = {
  'droit social':        '👷',
  'droit civil':         '⚖️',
  'droit commercial':    '🏢',
  'droit pénal':         '🔒',
  'procédure civile':    '📋',
  'droit fiscal':        '💶',
  'droit administratif': '🏛️',
};

const YearChart = ({ data }: { data: [string, number][] }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<{
    year: string; count: number; xPct: number; yPct: number
  } | null>(null);

  if (!data || data.length === 0) return null;

  const max  = Math.max(...data.map(([, v]) => v));
  const W    = 600;
  const H    = 100;
  const LEFT = 48;
  const barW = Math.max(3, Math.floor((W - LEFT - 10) / data.length) - 2);
  const gap  = Math.max(1, Math.floor((W - LEFT - 10) / data.length) - barW);
  const step = data.length > 15 ? Math.ceil(data.length / 8) : 1;
  const fmt  = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(r => ({
    v: Math.round(max * r),
    y: H - Math.round(r * H),
  }));

  return (
    <div className="w-full relative">
      {hovered && (
        <div
          className="pointer-events-none absolute z-50 -translate-x-1/2 px-2.5 py-1.5 rounded-lg bg-foreground text-background text-xs font-medium shadow-xl whitespace-nowrap"
          style={{ left: `${hovered.xPct}%`, bottom: `${(1 - hovered.yPct) * 100 + 3}%` }}
        >
          <span className="font-bold">{hovered.year}</span>
          {' · '}
          {t('graph.page.decisions_count', { formatted: hovered.count.toLocaleString(getDateLocale()) })}
          <div className="absolute left-1/2 -translate-x-1/2 top-full border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-foreground" />
        </div>
      )}
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H + 26}`}
          className="w-full"
          style={{ minWidth: '280px', overflow: 'visible' }}
          onMouseLeave={() => setHovered(null)}
        >
          {yTicks.map(({ v, y }) => (
            <g key={y}>
              <line x1={LEFT} y1={y} x2={W - 4} y2={y}
                stroke="currentColor" strokeOpacity={0.08} strokeWidth={1} />
              <text x={LEFT - 4} y={y + 3} textAnchor="end" fontSize={8}
                className="fill-muted-foreground">{fmt(v)}</text>
            </g>
          ))}
          {data.map(([year, count], i) => {
            const bh      = max > 0 ? Math.round((count / max) * H) : 0;
            const x       = LEFT + i * (barW + gap);
            const isHover = hovered?.year === year;
            return (
              <g key={year}
                onMouseEnter={() => setHovered({
                  year, count,
                  xPct: ((x + barW / 2) / W) * 100,
                  yPct: (H - bh) / (H + 26),
                })}
                style={{ cursor: 'pointer' }}
              >
                <rect x={x} y={0} width={barW} height={H} fill="transparent" />
                <rect x={x} y={H - bh} width={barW} height={bh} rx={2}
                  className={`fill-primary transition-opacity ${isHover ? 'opacity-100' : 'opacity-70'}`} />
                {i % step === 0 && (
                  <text x={x + barW / 2} y={H + 18} textAnchor="middle" fontSize={8}
                    className="fill-muted-foreground">{year}</text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const DomaineCard = ({ detail }: { detail: DomaineDetail }) => {
  const { t } = useTranslation();
  const locale   = getDateLocale();
  const total    = detail.count_cc + detail.count_ca;
  const pctCc    = total > 0 ? Math.round((detail.count_cc / total) * 100) : 0;
  const pctCa    = 100 - pctCc;
  const lastDate = detail.last_date
    ? new Date(detail.last_date).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <Link
      to={`/browse?domaine=${encodeURIComponent(detail.domaine)}`}
      className="rounded-xl border bg-card p-5 hover:bg-accent hover:border-primary/30 transition-all group flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{DOMAINE_ICONS[detail.domaine.toLowerCase()] || '📁'}</span>
        <div className="min-w-0">
          <p className="text-sm font-bold group-hover:text-primary transition-colors leading-tight">
            {detail.domaine}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('graph.page.decisions_count', { formatted: total.toLocaleString(locale) })}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex h-2 rounded-full overflow-hidden bg-muted">
          {pctCc > 0 && <div className="h-full bg-primary transition-all" style={{ width: `${pctCc}%` }} />}
          {pctCa > 0 && <div className="h-full bg-[#0e7490] transition-all" style={{ width: `${pctCa}%` }} />}
        </div>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-primary inline-block" />
            {t('graph.page.cassation_short')} · {detail.count_cc.toLocaleString(locale)}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#0e7490] inline-block" />
            {t('graph.page.appeal_short')} · {detail.count_ca.toLocaleString(locale)}
          </span>
        </div>
      </div>
      {lastDate && (
        <p className="text-[10px] text-muted-foreground/70 border-t pt-2 mt-auto">
          {t('graph.page.last_decision', { date: lastDate })}
        </p>
      )}
    </Link>
  );
};

export default function PublicLegalGraph() {
  const { stats, totalStr } = useStats();
  const { t } = useTranslation();
  const locale = getDateLocale();

  const pageTitle       = t('graph.page.meta_title');
  const pageDescription = t('graph.page.meta_description', { total: totalStr ?? '1M+' });

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`${SITE_URL}/legal-graph`} />
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url"         content={`${SITE_URL}/legal-graph`} />
        <meta property="og:type"        content="website" />
        <meta property="og:locale"      content={locale.replace('-', '_')} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <Breadcrumb items={[
          { label: t('articles.home'), to: '/' },
          { label: 'Legal Graph' },
        ]} />

        <main className="flex-1">

          {/* ── Hero ── */}
          <section className="border-b bg-muted/30 py-16 sm:py-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                {stats ? t('graph.page.hero_badge', { formatted: stats.total.toLocaleString(locale) }) : t('graph.page.hero_badge_fallback')}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                <Trans t={t} i18nKey="graph.page.hero_title" components={{ hl: <span className="text-primary" /> }} />
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t('graph.page.hero_intro')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <Link to="/"
                  className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20">
                  {t('graph.page.cta_search')}
                </Link>
                <Link to="/browse"
                  className="px-6 py-3 rounded-xl border-2 hover:bg-accent transition-colors text-sm font-medium">
                  {t('graph.page.cta_browse')}
                </Link>
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 space-y-16">
            {!stats ? (
              <div className="flex justify-center py-24">
                <Loader className="!size-6" />
              </div>
            ) : (
              <>
                {/* ── Chiffres clés ── */}
                <section>
                  <h2 className="text-2xl font-bold mb-2">{t('graph.page.figures_title')}</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('graph.page.figures_subtitle')}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      {
                        value: `${(stats.total / 1000).toFixed(0)}k+`,
                        label: t('graph.page.fig_decisions'),
                        sub:   t('graph.page.fig_decisions_sub'),
                        color: 'text-primary',
                      },
                      {
                        value: (() => {
                          const years = stats.by_year as [string, number][] | undefined;
                          return years && years.length > 1
                            ? `${years[0][0]}–${years[years.length - 1][0]}`
                            : '—';
                        })(),
                        label: t('graph.page.fig_years'),
                        sub:   t('graph.page.fig_years_sub'),
                        color: 'text-amber-600 dark:text-amber-400',
                      },
                      {
                        value: `${stats.domaines?.length ?? 0}`,
                        label: t('graph.page.fig_domains'),
                        sub:   t('graph.page.fig_domains_sub'),
                        color: 'text-[#0e7490]',
                      },
                      {
                        value: `${((stats.citations_total ?? 0) / 1000).toFixed(0)}k`,
                        label: t('graph.page.fig_links'),
                        sub:   t('graph.page.fig_links_sub'),
                        color: 'text-violet-600 dark:text-violet-400',
                      },
                    ].map(({ value, label, sub, color }) => (
                      <div key={label} className="rounded-xl border bg-card p-5 text-center">
                        <p className={`text-3xl font-bold mb-1 ${color}`}>{value}</p>
                        <p className="text-xs font-semibold text-foreground mb-0.5">{label}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{sub}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ── Évolution par année ── */}
                {(stats.by_year ?? []).length > 0 && (
                  <section className="rounded-xl border bg-card p-6 sm:p-8">
                    <div className="flex items-start justify-between mb-1 gap-4">
                      <div>
                        <h2 className="text-xl font-bold">{t('graph.page.evolution_title')}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('graph.page.evolution_subtitle')}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0 mt-1">
                        {stats.by_year?.[0]?.[0] ?? '—'} → {stats.by_year?.at(-1)?.[0] ?? '—'}
                      </span>
                    </div>
                    <div className="mt-6">
                      <YearChart data={stats.by_year ?? []} />
                    </div>
                  </section>
                )}

                {/* ── Domaines ── */}
                {(stats.domaines_detail ?? []).length > 0 && (
                  <section>
                    <div className="flex items-end justify-between mb-2 gap-4">
                      <div>
                        <h2 className="text-2xl font-bold">{t('graph.page.fig_domains')}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('graph.page.domains_subtitle')}
                        </p>
                      </div>
                      <Link to="/browse"
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 flex-shrink-0">
                        {t('common.see_all')}
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                    {/* Légende teal */}
                    <div className="flex items-center gap-4 mb-5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm bg-primary inline-block" />
                        {t('graph.page.legend_cassation')}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm bg-[#0e7490] inline-block" />
                        {t('graph.page.legend_appeal')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {stats.domaines_detail!.map(detail => (
                        <DomaineCard key={detail.domaine} detail={detail} />
                      ))}
                    </div>
                  </section>
                )}

                {/* ── Top concepts ── */}
                {(stats.top_concepts ?? []).length > 0 && (
                  <section>
                    <h2 className="text-2xl font-bold mb-2">{t('graph.page.concepts_title')}</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      {t('graph.page.concepts_subtitle')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(stats.top_concepts as { concept: string; count: number }[]).slice(0, 30).map(({ concept, count }) => (
                        <Link
                          key={concept}
                          to={`/?q=${encodeURIComponent(concept)}`}
                          title={t('graph.page.occurrences', { formatted: count.toLocaleString(locale) })}
                          className="inline-flex items-center gap-1.5 text-sm bg-primary/10 text-primary px-3 py-1.5 rounded-full hover:bg-primary/20 transition-colors"
                        >
                          {concept}
                          <span className="text-xs opacity-60 font-mono">
                            {(count / 1000).toFixed(1)}k
                          </span>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── Legal Graph explication ── */}
                <section className="grid sm:grid-cols-3 gap-6">
                  {[
                    {
                      emoji: '🕸️',
                      title: t('graph.page.feat_citations_title'),
                      desc:  t('graph.page.feat_citations_desc'),
                      stat:  t('graph.page.feat_citations_stat', { value: `${((stats.citations_total ?? 0) / 1000).toFixed(0)}k` }),
                    },
                    {
                      emoji: '🏷️',
                      title: t('graph.page.feat_relations_title'),
                      desc:  t('graph.page.feat_relations_desc'),
                      stat:  t('graph.page.feat_relations_stat'),
                    },
                    {
                      emoji: '📊',
                      title: t('graph.page.feat_score_title'),
                      desc:  t('graph.page.feat_score_desc'),
                      stat:  t('graph.page.feat_score_stat'),
                    },
                  ].map(({ emoji, title, desc, stat }) => (
                    <div key={title} className="rounded-xl border bg-card p-6">
                      <div className="text-3xl mb-4">{emoji}</div>
                      <h3 className="font-bold text-base mb-2">{title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{desc}</p>
                      <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                        {stat}
                      </span>
                    </div>
                  ))}
                </section>

                {/* ── CTA ── */}
                <section className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-10 text-center">
                  <div className="max-w-xl mx-auto">
                    <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">
                      {t('graph.page.cta_eyebrow')}
                    </p>
                    <h2 className="text-2xl font-bold mb-3">
                      {t('graph.page.cta_title')}
                    </h2>
                    <p className="text-muted-foreground mb-8 leading-relaxed text-sm">
                      {t('graph.page.cta_desc')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link to="/pricing"
                        className="px-7 py-3.5 rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-700 transition-colors shadow-lg shadow-violet-600/20">
                        {t('graph.page.cta_pro')}
                      </Link>
                      <Link to="/login"
                        className="px-7 py-3.5 rounded-xl border-2 hover:bg-accent transition-colors text-sm font-medium">
                        {t('graph.page.cta_login')}
                      </Link>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                      {t('graph.page.cta_free')}
                    </p>
                  </div>
                </section>
              </>
            )}
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}